import type { TTSResponseData, ASRResponseData } from './api_client.js';
import {
  requestTTS,
  requestASR,
  getStoredOpenRouterKey,
  setStoredOpenRouterKey,
  getStoredBaseUrl,
  setStoredBaseUrl,
} from './api_client.js';
import { AudioRecorder } from './audio_recorder.js';

// Register PWA service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Preset examples for TTS
interface PresetPrompt {
  label: string;
  text: string;
  lang: 'en' | 'hi';
}

const TTS_PRESETS: PresetPrompt[] = [
  {
    label: '🏢 HMD Company Intro',
    text: "HMD is a Finnish company and Europe's largest smartphone manufacturer, originally known for making Nokia-branded phones before launching its own independent brand line.",
    lang: 'en',
  },
  {
    label: '📞 Incoming Call',
    text: 'Incoming voice call from Ramesh Kumar.',
    lang: 'en',
  },
  {
    label: '🔋 Low Battery',
    text: 'Battery level is low at fifteen percent. Please connect your charger.',
    lang: 'en',
  },
  {
    label: '⏰ Morning Alarm',
    text: 'Good morning! It is six thirty A M. Time to wake up.',
    lang: 'en',
  },
  {
    label: '💬 Hindi Greeting',
    text: 'नमस्ते भारत, आपका दिन शुभ और मंगलमय हो।',
    lang: 'hi',
  },
  {
    label: '🌡️ Hindi Weather',
    text: 'आज का तापमान पच्चीस डिग्री सेल्सियस रहने का अनुमान है।',
    lang: 'hi',
  },
  {
    label: '📩 Hindi SMS Alert',
    text: 'आपको भारतीय स्टेट बैंक से एक नया संदेश प्राप्त हुआ है।',
    lang: 'hi',
  },
];

// Preset tests for ASR (fetches audio samples and sends to MAI-Transcribe-2)
interface ASRPresetTest {
  label: string;
  sampleUrl: string;
  lang: string;
}

const ASR_PRESETS: ASRPresetTest[] = [
  {
    label: 'Test 1: English Prompt',
    sampleUrl: '/samples/english_prompt.wav',
    lang: 'en',
  },
  {
    label: 'Test 2: Pure Hindi Greeting (नमस्ते)',
    sampleUrl: '/samples/hindi_greeting.wav',
    lang: 'hi',
  },
  {
    label: 'Test 3: Hinglish Mixed',
    sampleUrl: '/samples/hinglish_sample.wav',
    lang: 'auto',
  },
];

// State
let activeTab: 'tts' | 'asr' = 'tts';
let currentAudioBlobUrl: string | null = null;
let recordingTimerId: number | null = null;
let recordingSeconds = 0;

// Elements
const tabTTS = document.getElementById('tabTTS') as HTMLButtonElement;
const tabASR = document.getElementById('tabASR') as HTMLButtonElement;
const panelTTS = document.getElementById('panelTTS') as HTMLElement;
const panelASR = document.getElementById('panelASR') as HTMLElement;

// TTS Elements
const ttsTextInput = document.getElementById('ttsTextInput') as HTMLTextAreaElement;
const sliderSpeed = document.getElementById('sliderSpeed') as HTMLInputElement;
const sliderVolume = document.getElementById('sliderVolume') as HTMLInputElement;
const speedValue = document.getElementById('speedValue') as HTMLElement;
const volumeValue = document.getElementById('volumeValue') as HTMLElement;
const btnSynthesize = document.getElementById('btnSynthesize') as HTMLButtonElement;
const ttsPlayerCard = document.getElementById('ttsPlayerCard') as HTMLElement;
const ttsAudioElement = document.getElementById('ttsAudioElement') as HTMLAudioElement;
const metricRTF = document.getElementById('metricRTF') as HTMLElement;
const metricLatencyVal = document.getElementById('metricLatencyVal') as HTMLElement;
const metricDuration = document.getElementById('metricDuration') as HTMLElement;
const ttsPillsContainer = document.getElementById('ttsPillsContainer') as HTMLElement;

// ASR Elements
const asrPillsContainer = document.getElementById('asrPillsContainer') as HTMLElement;
const asrLanguageSelect = document.getElementById('asrLanguageSelect') as HTMLSelectElement;
const btnRecordMic = document.getElementById('btnRecordMic') as HTMLButtonElement;
const micIcon = document.getElementById('micIcon') as HTMLElement;
const micStatusText = document.getElementById('micStatusText') as HTMLElement;
const vuMeterBar = document.getElementById('vuMeterBar') as HTMLElement;
const asrResultText = document.getElementById('asrResultText') as HTMLElement;
const asrMetaBadge = document.getElementById('asrMetaBadge') as HTMLElement;


// Settings Elements
const btnSettings = document.getElementById('btnSettings') as HTMLButtonElement;
const settingsModal = document.getElementById('settingsModal') as HTMLElement;
const btnCloseSettings = document.getElementById('btnCloseSettings') as HTMLButtonElement;
const btnSaveSettings = document.getElementById('btnSaveSettings') as HTMLButtonElement;
const settingOpenRouterKey = document.getElementById('settingOpenRouterKey') as HTMLInputElement;
const settingApiBaseUrl = document.getElementById('settingApiBaseUrl') as HTMLInputElement;

// Recorder Instance
const recorder = new AudioRecorder({
  onVolumeChange: (level) => {
    vuMeterBar.style.width = `${Math.round(level * 100)}%`;
  },
});

// Tab Switcher
function switchTab(tab: 'tts' | 'asr'): void {
  activeTab = tab;
  if (tab === 'tts') {
    tabTTS.classList.add('active');
    tabASR.classList.remove('active');
    panelTTS.style.display = 'flex';
    panelASR.style.display = 'none';
  } else {
    tabASR.classList.add('active');
    tabTTS.classList.remove('active');
    panelASR.style.display = 'flex';
    panelTTS.style.display = 'none';
  }
}

tabTTS.addEventListener('click', () => switchTab('tts'));
tabASR.addEventListener('click', () => switchTab('asr'));

// Populate TTS Example Pills
TTS_PRESETS.forEach((preset) => {
  const pill = document.createElement('button');
  pill.className = `pill-btn ${preset.lang === 'hi' ? 'hi' : ''}`;
  pill.textContent = preset.label;
  pill.addEventListener('click', () => {
    ttsTextInput.value = preset.text;
    ttsTextInput.focus();
  });
  ttsPillsContainer.appendChild(pill);
});

// Set initial text
ttsTextInput.value = TTS_PRESETS[0].text;

// Slider listeners
sliderSpeed.addEventListener('input', () => {
  speedValue.textContent = `${Number(sliderSpeed.value).toFixed(2)}x`;
});
sliderVolume.addEventListener('input', () => {
  volumeValue.textContent = `${Number(sliderVolume.value).toFixed(1)}x`;
});

// Synthesize & Play
async function handleSynthesize(): Promise<void> {
  const text = ttsTextInput.value.trim();
  if (!text) {
    alert('Please enter text to synthesize.');
    return;
  }

  btnSynthesize.disabled = true;
  btnSynthesize.innerHTML = '<span>⏳</span> Synthesizing on Wasm Vocoder...';

  try {
    const speed = parseFloat(sliderSpeed.value);
    const volume = parseFloat(sliderVolume.value);

    const result: TTSResponseData = await requestTTS(text, { speed, volume });

    if (currentAudioBlobUrl) {
      URL.revokeObjectURL(currentAudioBlobUrl);
    }
    currentAudioBlobUrl = URL.createObjectURL(result.audioBlob);

    ttsAudioElement.src = currentAudioBlobUrl;
    ttsPlayerCard.style.display = 'flex';
    ttsAudioElement.play().catch(() => {});

    metricRTF.textContent = result.realTimeFactor.toFixed(3);
    metricLatencyVal.textContent = `${result.synthesisTimeMs} ms`;
    metricDuration.textContent = `${result.durationSeconds.toFixed(2)} s`;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Synthesis failed';
    alert(`Synthesis Error: ${msg}`);
  } finally {
    btnSynthesize.disabled = false;
    btnSynthesize.innerHTML = '<span>🔊</span> Synthesize & Play';
  }
}

btnSynthesize.addEventListener('click', handleSynthesize);

// Populate ASR Preset Pills
ASR_PRESETS.forEach((preset) => {
  const pill = document.createElement('button');
  pill.className = `pill-btn ${preset.lang === 'hi' ? 'hi' : ''}`;
  pill.textContent = preset.label;
  pill.addEventListener('click', async () => {
    pill.disabled = true;
    const originalText = pill.textContent;
    pill.textContent = 'Transcribing...';

    try {
      asrResultText.textContent = 'Transcribing with MAI-Transcribe-2 over cloud...';
      asrLanguageSelect.value = preset.lang;

      // Play the genuine audio sample
      const audio = new Audio(preset.sampleUrl);
      audio.play().catch(() => {});

      // Fetch the 16 kHz sample WAV blob and send to ASR
      const response = await fetch(preset.sampleUrl);
      const audioBlob = await response.blob();

      const asrRes: ASRResponseData = await requestASR(audioBlob, preset.lang);
      displayASRResult(asrRes);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sample test failed';
      asrResultText.textContent = `Error: ${msg}`;
    } finally {
      pill.disabled = false;
      pill.textContent = originalText;
    }
  });
  asrPillsContainer.appendChild(pill);
});

// Handle Microphone Recording
async function toggleRecording(): Promise<void> {
  if (!recorder.recording) {
    try {
      await recorder.start();
      btnRecordMic.classList.add('recording');
      micIcon.textContent = '⏹️';
      micStatusText.textContent = 'Recording (00:00)... Tap to finish';
      recordingSeconds = 0;

      recordingTimerId = window.setInterval(() => {
        recordingSeconds++;
        const mins = String(Math.floor(recordingSeconds / 60)).padStart(2, '0');
        const secs = String(recordingSeconds % 60).padStart(2, '0');
        micStatusText.textContent = `Recording (${mins}:${secs})... Tap to stop`;
      }, 1000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Microphone permission denied';
      alert(`Microphone Error: ${msg}`);
    }
  } else {
    // Stop recording
    if (recordingTimerId !== null) {
      clearInterval(recordingTimerId);
      recordingTimerId = null;
    }

    btnRecordMic.classList.remove('recording');
    micIcon.textContent = '🎙️';
    micStatusText.textContent = 'Transcribing with MAI-Transcribe-2...';
    vuMeterBar.style.width = '0%';

    try {
      const audioBlob = await recorder.stop();
      const language = asrLanguageSelect.value;
      const res: ASRResponseData = await requestASR(audioBlob, language);
      displayASRResult(res);
      micStatusText.textContent = 'Transcription complete! Tap to speak again';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Transcription failed';
      asrResultText.textContent = `Error: ${msg}`;
      micStatusText.textContent = 'Tap microphone to retry';
    }
  }
}

btnRecordMic.addEventListener('click', toggleRecording);

function displayASRResult(result: ASRResponseData): void {
  if (result.success && result.text) {
    asrResultText.textContent = result.text;
    asrMetaBadge.textContent = `Lang: ${result.language || 'auto'} | Latency: ${result.latencyMs} ms | ${result.model}`;
  } else {
    asrResultText.textContent = result.error || 'No speech detected';
  }
}

// Settings Modal Management
function openSettings(): void {
  settingOpenRouterKey.value = getStoredOpenRouterKey();
  settingApiBaseUrl.value = getStoredBaseUrl();
  settingsModal.classList.add('open');
}

function closeSettings(): void {
  settingsModal.classList.remove('open');
}

btnSettings.addEventListener('click', openSettings);
btnCloseSettings.addEventListener('click', closeSettings);
settingsModal.addEventListener('click', (e) => {
  if (e.target === settingsModal) closeSettings();
});

btnSaveSettings.addEventListener('click', () => {
  setStoredOpenRouterKey(settingOpenRouterKey.value);
  setStoredBaseUrl(settingApiBaseUrl.value);
  closeSettings();
  alert('Settings saved!');
});
